/**
 * Web-only guidance for a member whose subscription lives on a native store
 * purchase this account has never restored (PR #390 edge-case Q3, 2026-07-17).
 *
 * The case: someone buys at the native pre-login paywall on an anonymous uid,
 * never creates an account there, and later signs in on WEB with a different
 * account. PaidAccessGate walls them with no self-recovery — restore is a
 * store-receipt operation, so it only exists in the native app, and re-buying
 * via Stripe would double-bill (two unrelated RevenueCat customers, nothing to
 * dedupe server-side). The wall should point the way out: sign in to the app
 * on the phone with THIS account and tap Restore purchases there, which lets
 * RevenueCat transfer the store subscription onto this uid.
 *
 * Renders nothing on native — the native surfaces already carry the Restore
 * button, so the line would only compete with it.
 */
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { emphasizeBrand } from '../../lib/emphasizeBrand';

export const WEB_RESTORE_GUIDANCE_COPY =
  'Started your membership on your phone? Open the enough. app and sign in there with this same account — then tap Restore purchases to bring it over.';

interface WebRestoreGuidanceProps {
  /** `'dark'` for the green funnel chrome (sand text); `'light'` (default) for sand/cloud backdrops. */
  tone?: 'light' | 'dark';
  className?: string;
}

export const WebRestoreGuidance: React.FC<WebRestoreGuidanceProps> = ({ tone = 'light', className = '' }) => {
  if (Capacitor.isNativePlatform()) return null;
  const toneClass = tone === 'dark' ? 'text-[13px] text-sand/70' : 'text-sm text-growth/70';
  return (
    <p data-testid="web-restore-guidance" className={`text-center ${toneClass} ${className}`.trim()}>
      {emphasizeBrand(WEB_RESTORE_GUIDANCE_COPY)}
    </p>
  );
};
